import React, { useEffect, useState } from "react"
import { Button, CircularProgress } from "@material-ui/core"
import Grid from "@material-ui/core/Grid"
import { Toast } from "components/shared/Toaster"
import InputFieldDetailed from "components/forms/Input"
import strings from "strings"
import Auth from "tools/AuthService"
import { trackSignupConversion } from "analytics/googleAds"
import { captureSignupCompleted } from "posthog/posthogConfig"
import OtpCodeInput from "../OtpCodeInput/OtpCodeInput"
import TypoGraph from "../../forms/typograph"
import logo from "../../../styles/images/logo.svg"
import "../SelfRegister/SelfRegister.css"

const t = strings.LOGIN_STRING
// Cooldown (seconds) applied to the "Resend code" link. Purely UX — the backend
// also rate-limits (5 requests / IP / 15 min).
const RESEND_COOLDOWN_SECONDS = 45

const isValidEmail = (value) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value)

// Accepts only internal paths; rejects absolute/protocol-relative URLs and
// dangerous schemes to prevent open-redirect abuse.
const sanitizeTarget = (raw) => {
  if (!raw) return null
  const target = raw.trim()
  if (!target.startsWith("/") || target.startsWith("//")) return null
  if (target.includes(":")) return null
  return target
}

// Maps a register-otp/verify-otp rejection (the raw fetch Response) to a
// user-facing message. Covers the register-otp status codes (429 rate limit,
// 404 registration disabled, 400 invalid email) and the verify-otp business
// errors ("Invalid code" / "Code expired").
const registerErrorMessage = async (err) => {
  if (!err || typeof err.status !== "number") return t.OTP_ERROR_GENERIC
  if (err.status === 429) return t.REGISTER_RATE_LIMIT
  if (err.status === 404) return t.REGISTER_DISABLED
  if (err.status >= 500) return t.OTP_ERROR_GENERIC
  let body
  try {
    body = await err.json()
  } catch {
    return t.OTP_ERROR_GENERIC
  }
  if (Array.isArray(body)) return t.OTP_EMAIL_INVALID // bean-validation error shape
  const message = body?.message
  if (message === "Code expired") return t.OTP_EXPIRED
  if (message === "Invalid code") return t.OTP_INVALID
  // The address already belongs to an account that two-factor keeps on the password flow.
  if (err.status === 403) return t.TWO_FACTOR_PASSWORD_REQUIRED
  return t.OTP_ERROR_GENERIC
}

/**
 * Passwordless registration for campaign leads (public /login/register-otp).
 * Screen 1 (email) → register-otp; screen 2 (code) → verify-otp. verify-otp
 * stores the session tokens (Authorization/Refresh_token) exactly like the
 * normal login, then we hand off to onLogIn(). The freshly registered account
 * comes back as status === "Pending", so App shows the dismissable
 * complete-profile reminder (<CompleteProfileModal />) over the app.
 */
const RegisterOtp = ({ onLogIn, redirectToLogin, history }) => {
  const [email, setEmail] = useState("")
  const [otpCode, setOtpCode] = useState("")
  const [step, setStep] = useState("email") // "email" | "code"
  const [loading, setLoading] = useState(false)
  const [resendCooldown, setResendCooldown] = useState(0)

  // Optional ?email= deep-link from the campaign link: pre-fill and move to the
  // code step, but never auto-request a code (mail scanners/prefetch would burn
  // the throttle). Strip the param so the email doesn't linger in history.
  useEffect(() => {
    const search = history?.location?.search
    if (!search) return
    const params = new URLSearchParams(search)
    const emailParam = (params.get("email") || "").trim()
    // Optional ?target= deep-link consumed by handleLogin (App.js) after verify.
    const target = sanitizeTarget(params.get("target"))
    if (target) {
      localStorage.setItem("redirectUrl", target)
      sessionStorage.setItem("deepLinkRedirect", "true")
    }
    if (emailParam && isValidEmail(emailParam)) {
      setEmail(emailParam)
    }
    window.history.replaceState(null, "", "/login/register-otp")
  }, []) // eslint-disable-line react-hooks/exhaustive-deps

  // Countdown for the "Resend code" link.
  useEffect(() => {
    if (resendCooldown <= 0) return undefined
    const timer = setInterval(() => {
      setResendCooldown((s) => (s <= 1 ? 0 : s - 1))
    }, 1000)
    return () => clearInterval(timer)
  }, [resendCooldown])

  // Requests a registration code. Used by screen 1 submit and "Resend code".
  // Always lands on the code screen with a neutral message (anti-enumeration).
  const requestCode = () => {
    const normalizedEmail = email.trim()
    if (!isValidEmail(normalizedEmail)) {
      Toast({ type: strings.ERROR, message: t.OTP_EMAIL_INVALID })
      return
    }
    setLoading(true)
    Auth.registerOtp(normalizedEmail)
      .then(() => {
        Toast({ type: "info", message: t.REGISTER_CODE_SENT })
        setStep("code")
        setResendCooldown(RESEND_COOLDOWN_SECONDS)
      })
      .catch(async (err) => {
        Toast({ type: strings.ERROR, message: await registerErrorMessage(err) })
      })
      .finally(() => setLoading(false))
  }

  // Screen 2: verify the code. On success the tokens are already stored by
  // Auth.verifyOtp, so we just reuse the shared post-login handler.
  const verifyCode = () => {
    const normalizedCode = otpCode.trim()
    if (!normalizedCode) {
      Toast({ type: strings.ERROR, message: t.OTP_CODE_REQUIRED })
      return
    }
    setLoading(true)
    Auth.verifyOtp(email.trim(), normalizedCode)
      .then(() => {
        trackSignupConversion()
        captureSignupCompleted('register_otp')
        onLogIn()
      })
      .catch(async (err) => {
        Toast({ type: strings.ERROR, message: await registerErrorMessage(err) })
        setLoading(false)
      })
  }

  const handleSubmit = (e) => {
    e.preventDefault()
    if (step === "email") {
      requestCode()
    } else {
      verifyCode()
    }
  }

  const handleResend = () => {
    if (resendCooldown <= 0 && !loading) requestCode()
  }

  const handleChangeEmail = () => {
    setStep("email")
    setOtpCode("")
    setResendCooldown(0)
  }

  return (
    <Grid item xs={12} sm={6} md={6} lg={6} className="gs-form-col" style={{ zIndex: 1 }}>
      <form name="form" onSubmit={handleSubmit} className="self-register-container gs-card">

        <img className="gs-form-logo" src={logo} alt="ROX" />

        <div className="gs-card-head">
          <TypoGraph
            variant="h4"
            content={step === "email" ? t.REGISTER_TITLE : t.REGISTER_CODE_TITLE}
            className="gs-title"
          />
          <TypoGraph
            variant="body1"
            content={step === "email" ? t.REGISTER_EMAIL_HINT : `${t.REGISTER_CODE_SENT} ${email}`}
            className="gs-subtitle"
            style={{ wordBreak: "break-word" }}
          />
        </div>

        {step === "email" && (
          <div className="gs-field">
            <TypoGraph variant="body1" content={"Work email"} className={"gs-label"} />
            <InputFieldDetailed
              fullWidth
              id="email"
              label=""
              name="email"
              autoComplete="email"
              placeholder="jane@company.com"
              className="gs-input-field"
              value={email}
              variant="outlined"
              size="small"
              disabled={loading}
              hideValidationText
              onChange={(e) => setEmail(e.target.value)}
            />
          </div>
        )}

        {step === "code" && (
          <>
            <div className="gs-field">
              <TypoGraph variant="body1" content={"Code"} className={"gs-label"} />
              <OtpCodeInput value={otpCode} onChange={setOtpCode} disabled={loading} />
              <div className="gs-hint">{t.REGISTER_CODE_HINT}</div>
            </div>

            <div
              className="gs-link-action"
              role="button"
              tabIndex={0}
              onClick={handleResend}
              onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); handleResend() } }}
              style={{
                cursor: resendCooldown > 0 ? "default" : "pointer",
                opacity: resendCooldown > 0 ? 0.6 : 1,
              }}
            >
              {resendCooldown > 0 ? `${t.OTP_RESEND_IN} ${resendCooldown}s` : t.OTP_RESEND}
            </div>
            <div
              className="gs-link-action"
              role="button"
              tabIndex={0}
              onClick={handleChangeEmail}
              onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); handleChangeEmail() } }}
              style={{ cursor: "pointer" }}
            >
              {t.OTP_CHANGE_EMAIL}
            </div>
          </>
        )}

        <Button
          fullWidth
          variant="contained"
          color="primary"
          className="gs-cta"
          type="submit"
          disabled={loading}
        >
          {loading ? <CircularProgress size={25} color="inherit" />
            : step === "email" ? t.REGISTER_SEND_CODE
            : t.REGISTER_VERIFY}
        </Button>

        <Grid container justifyContent="center" spacing={2} style={{ marginTop: "1rem" }}>
          <TypoGraph
            variant="body2"
            content={t.REGISTER_BACK_TO_LOGIN}
            className="sign-up-button"
            onClick={redirectToLogin}
          />
        </Grid>
      </form>
    </Grid>
  )
}

export default RegisterOtp
